"use client";

/**
 * Typed client for `GET /api/v1/report/explain` (mission drilldown-substrate, WP05/T017).
 *
 * Same conventions as `../api/client.ts`'s `reportGet` (same-origin fetch, bearer token from
 * the `access_token` cookie, `{error}` envelope surfaced as `ApiError`, un-enveloped success
 * body) — duplicated in miniature here rather than importing `reportGet` because that helper
 * isn't exported (`../api/client.ts` is out of this WP's owned-files map; a private helper
 * export would be an unnecessary out-of-map edit for a ~20-line function). `getCookie`/`ApiError`
 * themselves ARE shared, ownerless library modules — imported directly, not duplicated.
 *
 * `year=0` is a REAL value for the §6 permit tables (contract rule) — every param is appended
 * with an explicit `!== undefined`/`!== null` check, never a truthy check, so a year of 0 is
 * never silently dropped from the query string.
 */

import { ApiError } from "@/types/api";
import { getCookie } from "@/lib/cookies";
import {
  explainResultSchema,
  type CellAddress,
  type ExplainResult,
} from "./explainTypes";

export class ExplainContractError extends Error {
  readonly issues: string[];

  constructor(issues: string[]) {
    super(`Explain API contract violation: ${issues.join("; ")}`);
    this.name = "ExplainContractError";
    this.issues = issues;
  }
}

export interface FetchExplainParams {
  runId: string;
  address: CellAddress;
  page?: number;
  pageSize?: number;
  stepKey?: string | null;
}

function explainUrl(params: FetchExplainParams): string {
  const qs = new URLSearchParams();
  qs.set("run_id", params.runId);
  qs.set("component", params.address.component);
  qs.set("key", params.address.key);
  qs.set("metric", params.address.metric);
  if (params.address.year !== null && params.address.year !== undefined) {
    qs.set("year", String(params.address.year));
  }
  if (
    params.address.scenario !== null &&
    params.address.scenario !== undefined
  ) {
    qs.set("scenario", params.address.scenario);
  }
  if (params.page !== undefined) qs.set("page", String(params.page));
  if (params.pageSize !== undefined)
    qs.set("page_size", String(params.pageSize));
  if (params.stepKey) qs.set("step_key", params.stepKey);
  return `/api/v1/report/explain?${qs.toString()}`;
}

export async function fetchExplain(
  params: FetchExplainParams,
): Promise<ExplainResult> {
  const requestId = crypto.randomUUID();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Request-ID": requestId,
  };
  const token = getCookie("access_token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(explainUrl(params), { method: "GET", headers });

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new ApiError(
      {
        code: "PARSE_ERROR",
        message: `Non-JSON response (${response.status} ${response.statusText})`,
        requestId,
      },
      response.status,
    );
  }

  if (!response.ok) {
    const body = json as {
      error?: { code: string; message: string; request_id?: string };
    };
    if (body.error) {
      throw new ApiError(
        {
          code: body.error.code,
          message: body.error.message,
          requestId: body.error.request_id ?? requestId,
        },
        response.status,
      );
    }
    throw new ApiError(
      { code: "UNKNOWN", message: response.statusText, requestId },
      response.status,
    );
  }

  const parsed = explainResultSchema.safeParse(json);
  if (!parsed.success) {
    throw new ExplainContractError(
      parsed.error.issues.map(
        (issue) => `${issue.path.join(".")}: ${issue.message}`,
      ),
    );
  }
  return parsed.data;
}
